<template>
    <section name="CreateEditNotice">
        <form-modal
            :title="title"
            v-model="modelValue"
            :submit-button="'Submit'"
            @submit="submitForm">
            <section class="vstack gap-3">
                <div>
                    <label for="agency">Agency *</label>
                    <SelectField
                        name="agency"
                        class="w-100"
                        v-model="form.agency"
                        :select-items="dcraOris"
                        mode="single"
                        :error="errors.agency"
                        :min="1"></SelectField>
                </div>
                <div>
                    <label for="year">Year *</label>
                    <SelectField
                        name="year"
                        class="w-100"
                        v-model="form.year"
                        :select-items="years"
                        mode="single"
                        :error="errors.year"
                        :min="1"
                        @update:modelValue="handleYearUpdate()"></SelectField>
                </div>
                <div>
                    <label for="quarter">Quarter *</label>
                    <SelectField
                        name="quarter"
                        class="w-100"
                        v-model="form.quarter"
                        :select-items="quarters"
                        mode="single"
                        :error="errors.quarter"
                        :min="1"
                        :open-direction="'top'"></SelectField>
                </div>
            </section>
        </form-modal>
    </section>
</template>

<script setup lang="ts">
import {ref, computed, defineProps, watch, onMounted} from "vue";
import {Death, DeathForm, DeathZeroReport} from "./schema";
import {useDcraStore} from "@/stores/dcraStore";
import FormModal from "../Misc/FormModal.vue";
import SelectField from "@/components/Misc/SelectField.vue";
import {BInput, BTextarea, BAgency, BDatePicker} from "@/components/Fields";
import {array, date, number, object, string, ValidationError} from "yup";
import {useFormValidation} from "@/validation";
import {SelectItem, UploadType} from "@/types";

const modelValue = defineModel({type: Boolean});
const props = defineProps<{
    startDate: string;
}>();
const emit = defineEmits(["dismiss", "submit", "update:modelValue"]);
var dcraStore = useDcraStore();

const {handleSubmit, errors, resetValidation} = useFormValidation();

const form = ref({
    agency: "",
    year: new Date().getFullYear(),
    quarter: 0,
} as DeathZeroReport);

const schema = object<DeathForm>({
    agency: string().required("Agency is a required field"),
    year: number()
        .transform((value) => (Number.isNaN(value) ? null : value))
        .min(new Date(props.startDate).getFullYear())
        .max(new Date().getFullYear())
        .required("Year is a required field"),
    quarter: number().required("Quarter is a required field"),
});

var dcraErrors = ref([] as string[]);

var dcraOris = ref([] as SelectItem[]);
var quarters = ref([] as SelectItem[]);
//Computed

const title = "Add Zero Report";
const years = computed(()=> {
    let years = [] as SelectItem[];

    for(var i = new Date(props.startDate).getFullYear(); i<= new Date().getFullYear(); i++ ){
        years.push({label: `${i}`, value: i} as SelectItem)
    }

    return years;
})
//FUNctions
const getQuarters = () => {    
    quarters.value = [] as SelectItem[];
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    const currentQuarter = Math.floor(currentMonth / 3) + 1;

    if (form.value.year < currentYear) {
        quarters.value =  [
            {label: "Q1", value: 1},
            {label: "Q2", value: 2},
            {label: "Q3", value: 3},
            {label: "Q4", value: 4},
        ] as SelectItem[];
    }

    if (form.value.year === currentYear) {
        quarters.value = Array.from({length: currentQuarter}, (_, i) => {
            return {label: `Q${i + 1}`, value: i + 1} as SelectItem;
        });
    };
};

const submitForm = async () => {
    if (await handleSubmit(schema, form.value)) {
        await dcraStore.addZeroReport(form.value);

        window.location.href = "dataupload.aspx?source=8";
    }
};

const handleYearUpdate = () => {
    form.value.quarter = -1;
    getQuarters();
}

const getOriList = async () => {
    let data = await dcraStore.getOriListForDataset("DCRA");

    dcraOris.value = data.map(ori=> {
        return {label:`${ori.ori} - ${ori.label}`, value:ori.ori} as SelectItem;
    });
}
//LifeCycle Hooks
onMounted(async () => {
    await getOriList();

    getQuarters();
});

watch(
    () => form.value,
    async () => {
        
        try {
            if (errors.value.length == 0) {
                return;
            }

            errors.value = Array.isArray(errors.value) ? [] : {};
            await schema.validate(form.value, {abortEarly: false});
        } catch (err) {
            const error = err as ValidationError;
            if (error.name !== "ValidationError") throw err;

            // Build an object map of field -> message
            const newErrors: Record<string, string> = {};
            error.inner.forEach((it: ValidationError) => {
                if (it.path) newErrors[it.path] = it.message;
            });
            errors.value = newErrors;
        }
    },
    {deep: true}
);
</script>

<style scoped>
.dcra-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}
fieldset {
    border: 1px solid #ccc;
    padding: 1rem;
}
legend {
    font-weight: bold;
}
label {
    display: block;
    margin-bottom: 0.25rem;
}
input,
select,
textarea {
    width: 100%;
    padding: 0.25rem;
    box-sizing: border-box;
}
button {
    align-self: flex-start;
    padding: 0.5rem 1rem;
}
</style>
